import moment from 'moment'
import { HolderOutlined } from '@ant-design/icons'
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { IContractReportFieldOption, IContractReportsDrawer } from './types'
import { useDeviceType } from '@hooks/useDeviceType'
import { ModalConfirm } from '@primitive/modal.confirm'
import { ModalPreview } from '@primitive/modal.preview'

import imgEmpty from '@img/empty.svg'
import { IconClearClose, IconDelete, IconDownload, IconReports } from '@img/icons'
import { Button, Checkbox, Divider, Drawer, Empty, Flex, Loader, Modal, Table, ToolTip } from '@ui/index'

import css from './ui.reports.module.scss'

const SortableReportField = ({ field, position, disabled, onChange }: {
    field: IContractReportFieldOption
    position: number
    disabled: boolean
    onChange: (fieldName: string) => void
}) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: field.fieldName,
        disabled,
    })

    return <div
        ref={setNodeRef}
        className={`${css['field']} ${css['field_selected']} ${isDragging ? css['field_dragging'] : ''}`}
        style={{ transform: CSS.Transform.toString(transform), transition }}
        data-field-name={field.fieldName}
        data-field-position={position}
    >
        <ToolTip title='Изменить порядок поля' placement='bottomLeft'>
            <button
                ref={setActivatorNodeRef}
                type='button'
                className={css['drag_handle']}
                {...attributes}
                {...listeners}
                aria-label={`Переместить поле «${field.fieldLabel}»`}
                disabled={disabled}
            >
                <HolderOutlined aria-hidden />
            </button>
        </ToolTip>
        <label className={css['selected_field_label']}>
            <span className={css['field_number']} aria-hidden>{position}.</span>
            <Checkbox checked={field.isSelected} isDisabled={disabled} onChange={() => onChange(field.fieldName)} />
            <span className={css['field_names']} title={field.fieldLabel}>
                <span>{field.fieldLabel}</span>
            </span>
        </label>
    </div>
}

export const ContractReportsDrawer = ({
    isOpen,
    isLoading,
    isCreating,
    creationProgress,
    fields,
    reports,
    modalConfirm,
    modalPreview,
    handleButtonCancel,
    handleFieldChange,
    handleFieldMove,
    handleFieldsAllChange,
    handleCreate,
    handlePreview,
    handleDownload,
    handleDelete,
}: IContractReportsDrawer) => {
    const deviceType = useDeviceType()
    const selectedFields = fields.filter((field) => field.isSelected)
    const unselectedFields = fields.filter((field) => !field.isSelected)
    const selectedFieldsCount = selectedFields.length
    const isAllFieldsSelected = fields.length > 0 && selectedFieldsCount === fields.length
    const progress = Math.min(100, Math.max(0, Math.floor(creationProgress.progress)))
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )
    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (over && active.id !== over.id) handleFieldMove(String(active.id), String(over.id))
    }

    return <>
        <Drawer isOpen={isOpen} placement='bottom' size='99.4%'>
            <Flex className='drawer_container'>
                <Flex flexDirection='row' alignItems='center' justifyContent='space-between' className='drawer_container-header'>
                    <header>
                        <Flex gap='5px' flexDirection='row' alignItems='center'>
                            <IconReports color='#fff' />
                            <h4>Отчёты по договорам</h4>
                        </Flex>
                    </header>

                    <Button variant='icon' size='md' onClick={handleButtonCancel}>
                        <IconClearClose />
                    </Button>
                </Flex>

                <Flex gap='4px' className='drawer_container-body_bottom_panel'>
                    <div className={css['content']}>
                        <section className={css['fields_section']}>
                            <div className={css['section_header']}>
                                <div>
                                    <h5>Поля отчёта</h5>
                                </div>

                                <label className={css['select_all']}>
                                    <Checkbox
                                        checked={isAllFieldsSelected}
                                        isDisabled={fields.length === 0 || isLoading || isCreating}
                                        onChange={handleFieldsAllChange}
                                    />
                                    <span>Выбрать все</span>
                                </label>
                            </div>

                            {fields.length === 0
                                ? <Empty image={imgEmpty} title='Поля отсутствуют' />
                                : <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <div className={css['fields_grid']}>
                                        <section className={`${css['field_group']} ${css['field_group_selected']}`} aria-label='Выбранные поля'>
                                            <h6 className={css['field_group_title']}>Выбранные поля</h6>
                                            <div className={css['field_list']}>
                                                <SortableContext items={selectedFields.map(field => field.fieldName)} strategy={verticalListSortingStrategy}>
                                                    {selectedFields.map((field, index) => (
                                                        <SortableReportField
                                                            key={field.fieldName}
                                                            field={field}
                                                            position={index + 1}
                                                            disabled={isLoading || isCreating}
                                                            onChange={handleFieldChange}
                                                        />
                                                    ))}
                                                </SortableContext>
                                                {selectedFields.length === 0 && <p className={css['field_empty']}>Нет выбранных полей</p>}
                                            </div>
                                        </section>

                                        <section className={css['field_group']} aria-label='Не выбранные поля'>
                                            <h6 className={css['field_group_title']}>Не выбранные поля</h6>
                                            <div className={css['field_list']}>
                                                {unselectedFields.map((field) => (
                                                    <label key={field.fieldName} className={css['field']}>
                                                        <Checkbox
                                                            checked={field.isSelected}
                                                            isDisabled={isLoading || isCreating}
                                                            onChange={() => handleFieldChange(field.fieldName)}
                                                        />
                                                        <span className={css['field_names']} title={field.fieldLabel}>
                                                            <span>{field.fieldLabel}</span>
                                                        </span>
                                                    </label>
                                                ))}
                                                {unselectedFields.length === 0 && <p className={css['field_empty']}>Все поля выбраны</p>}
                                            </div>
                                        </section>
                                    </div>
                                </DndContext>
                            }
                        </section>

                        <section className={css['reports_section']} aria-label='Сформированные отчёты'>
                            <Table.Container>
                                <Table.Header paddingLeft='8px' paddingRight='86px' classNameContent={css['table_header']}>
                                    <p>Наименование файла<br />Размер файла</p>
                                    <p className={css['hidden_500']} style={{textAlign: 'center'}}>Дата создания</p>
                                </Table.Header>

                                <Table.Content>
                                    {reports.length === 0
                                        ? <Empty image={imgEmpty} title='Отчёты отсутствуют' />
                                        : reports.map((report) => (
                                            <Table.Row
                                                key={report.contractReportID}
                                                className={css['table_row']}
                                                onClick={() => handlePreview(report.contractReportID)}
                                            >
                                                <div className={css['file_name']}>
                                                    <p>{report.fileName}</p>
                                                    <p>Размер файла: {report.fileSize}</p>
                                                </div>

                                                <div className={css['hidden_500']} style={{textAlign: 'center'}}>
                                                    <p>{moment(report.dateCreate).format('DD.MM.YYYY')}</p>
                                                    <p>{moment(report.dateCreate).format('HH:mm:ss')}</p>
                                                </div>

                                                <Flex gap='2px' flexDirection='row' alignItems='center' justifyContent='center'>
                                                    <ToolTip title='Скачать' placement='bottomRight'>
                                                        <Button
                                                            variant='icon'
                                                            size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                                            onClick={(event) => {
                                                                handleDownload(report.contractReportID)
                                                                event.stopPropagation()
                                                            }}
                                                        >
                                                            <IconDownload width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                                                        </Button>
                                                    </ToolTip>

                                                    <ToolTip title='Удалить' placement='bottomRight'>
                                                        <Button
                                                            variant='icon'
                                                            size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                                            onClick={(event) => {
                                                                handleDelete(report.contractReportID)
                                                                event.stopPropagation()
                                                            }}
                                                        >
                                                            <IconDelete width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                                                        </Button>
                                                    </ToolTip>
                                                </Flex>
                                            </Table.Row>
                                        ))
                                    }
                                </Table.Content>
                            </Table.Container>
                        </section>
                    </div>

                    <Divider />
                    <Flex flexDirection='row' justifyContent='flex-end'>
                        <Button padding='0 14px' isLoading={isLoading || isCreating} isDisabled={isLoading || isCreating} onClick={handleCreate}>
                            Создать отчёт
                        </Button>
                    </Flex>
                </Flex>

                <Loader isOpen={isLoading} backdrop title='Формирование отчёта...' />
            </Flex>
        </Drawer>

        <Modal isOpen={isCreating} zIndex='1070' maxWidth='430px'>
            <div className={`modal_container ${css['progress_modal']}`} role='dialog' aria-modal='true' aria-labelledby='contract-report-progress-title'>
                <div className='modal_container-header'>
                    <h4 id='contract-report-progress-title'>Создание отчёта</h4>
                </div>

                <div className={css['progress_content']} aria-live='polite'>
                    <div className={css['progress_status']}>
                        <p>{creationProgress.message}</p>
                        <strong>{progress}%</strong>
                    </div>
                    <div
                        className={css['progress_track']}
                        role='progressbar'
                        aria-label='Прогресс создания отчёта'
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progress}
                        aria-valuetext={creationProgress.message}
                    >
                        <span className={css['progress_indicator']} style={{width: `${progress}%`}} />
                    </div>
                    <small>
                        {creationProgress.totalRows > 0
                            ? `Обработано строк: ${creationProgress.processedRows.toLocaleString('ru-RU')} из ${creationProgress.totalRows.toLocaleString('ru-RU')}`
                            : 'Пожалуйста, дождитесь завершения операции.'
                        }
                    </small>
                </div>
            </div>
        </Modal>

        <ModalConfirm {...modalConfirm} />
        <ModalPreview {...modalPreview} />
    </>
}